import { useMemo, useRef, useState } from 'react'
import { Box, Flex, Text, useT, Modal, toaster } from '@minetrace/ui'
import { useFormColors } from '@minetrace/ui/forms'
import {
  LuUpload as Upload,
  LuTrash2 as Trash2,
  LuPlus as Plus,
  LuFileCheck2 as FileCheck,
  LuCircleCheck as CircleCheck,
  LuTriangleAlert as AlertTriangle,
  LuCircleX as CircleX,
} from 'react-icons/lu'
import type { ShareholderResponse } from '../../../api/models/response/proveedor'
import type { AddShareholderRequest } from '../../../api/models/request/proveedor'

interface Props {
  shareholders: ShareholderResponse[]
  requiresEnhancedDueDiligence: boolean
  isReadonly?: boolean
  onAddFromCertificate: (file: File, participationPercent: number) => Promise<void>
  onUpdate: (id: string, data: AddShareholderRequest, file?: File) => Promise<void>
  onRemove: (id: string) => Promise<void>
}

type RowFields = Pick<AddShareholderRequest, 'documentType' | 'documentNumber' | 'firstName' | 'lastName'> & { participationPercent: string }

const DOC_TYPE_LABEL: Record<number, string> = { 1: 'DNI', 2: 'CE', 3: 'Pasaporte' }

// LIMPIO=1 verde; INVÁLIDO=2/RECHAZADO=4 rojo; ALERTAS=3/5/6/7 ámbar; PENDIENTE=0 gris
function statusColors(status: number): { bg: string; color: string; icon: React.ReactNode } {
  if (status === 1) return { bg: '#ECFDF5', color: '#047857', icon: <CircleCheck size={13} /> }
  if (status === 2 || status === 4) return { bg: '#fcefeb', color: '#b23a26', icon: <CircleX size={13} /> }
  if (status === 0) return { bg: '#efe8dd', color: '#857b71', icon: <AlertTriangle size={13} /> }
  return { bg: '#FFF7ED', color: '#EA580C', icon: <AlertTriangle size={13} /> }
}

export default function Step10Ubo({
  shareholders, requiresEnhancedDueDiligence, isReadonly = false,
  onAddFromCertificate, onUpdate, onRemove,
}: Props) {
  const c = useFormColors()
  const { t } = useT()
  const [edits, setEdits] = useState<Record<string, RowFields>>({})
  const [busy, setBusy] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)

  const tt = (key: string, fallback: string, values?: Record<string, unknown>) => {
    const full = `proveedores.wizard.step10.${key}`
    const r = t(full, values)
    return r === full ? fallback : r
  }

  const rowFields = (sh: ShareholderResponse): RowFields =>
    edits[sh.id] ?? {
      documentType: sh.documentType,
      documentNumber: sh.documentNumber,
      firstName: sh.firstName,
      lastName: sh.lastName,
      participationPercent: String(sh.participationPercent),
    }

  const rowFieldsById = (id: string): RowFields => rowFields(shareholders.find(s => s.id === id)!)

  const isDirty = (sh: ShareholderResponse): boolean => {
    const f = edits[sh.id]
    if (!f) return false
    return f.firstName !== sh.firstName || f.lastName !== sh.lastName
      || Number(f.participationPercent) !== sh.participationPercent
  }

  const total = useMemo(
    () => shareholders.reduce((s, sh) => s + (Number(rowFields(sh).participationPercent) || 0), 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [shareholders, edits],
  )

  const exceeds = total > 100.001
  const hasInvalid = shareholders.some(s => s.validationStatus === 2 || s.validationStatus === 4)

  const setEdit = (id: string, patch: Partial<RowFields>) =>
    setEdits(prev => ({ ...prev, [id]: { ...rowFieldsById(id), ...patch } }))

  const saveRow = async (sh: ShareholderResponse) => {
    const f = rowFields(sh)
    // La columna es numeric(5,2) y el registro exige 0 < % <= 100: un 1000 o un negativo
    // pasaba al staging y abortaba la aprobación entera de la solicitud.
    const pct = Number(f.participationPercent)
    if (!Number.isFinite(pct) || pct <= 0 || pct > 100) {
      toaster.error({ description: 'La participación debe ser mayor que 0 y como máximo 100%.' })
      return
    }
    setBusy(true)
    try {
      await onUpdate(sh.id, {
        documentType: f.documentType,
        documentNumber: f.documentNumber.trim(),
        firstName: f.firstName.trim(),
        lastName: f.lastName.trim(),
        participationPercent: Math.round(pct * 100) / 100,
      })
      setEdits(prev => { const n = { ...prev }; delete n[sh.id]; return n })
    } finally { setBusy(false) }
  }

  const replaceCertificate = async (sh: ShareholderResponse, file: File) => {
    const f = rowFields(sh)
    setBusy(true)
    try {
      await onUpdate(sh.id, {
        documentType: f.documentType,
        documentNumber: f.documentNumber.trim(),
        firstName: f.firstName.trim(),
        lastName: f.lastName.trim(),
        participationPercent: Number(f.participationPercent) || 0,
      }, file)
    } finally { setBusy(false) }
  }

  const removeRow = async (id: string) => {
    setBusy(true)
    try { await onRemove(id) } finally { setBusy(false) }
  }

  const thStyle: React.CSSProperties = {
    textAlign: 'left', padding: '8px 12px', color: c.textMuted, fontWeight: 600,
    fontSize: '11px', textTransform: 'uppercase', borderBottom: `1px solid ${c.border}`,
  }
  const tdStyle: React.CSSProperties = { padding: '8px 12px', verticalAlign: 'middle', borderBottom: `1px solid ${c.border}` }
  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '6px 8px', fontSize: '13px', borderRadius: '6px',
    border: `1px solid ${c.border}`, background: 'white', color: c.text, outline: 'none',
  }

  return (
    <Box>
      {/* Header */}
      <Flex justifyContent="space-between" alignItems="flex-start" mb={4} gap={4} flexWrap="wrap">
        <Box maxW="2xl">
          <Text fontSize="sm" color={c.textSecondary}>
            {tt('intro', 'Por normativa de cumplimiento y debida diligencia (Ley de Lavado de Activos / Estándar LBMA), es obligatorio registrar a los accionistas y adjuntar su Certificado Único Laboral vigente.')}
          </Text>
        </Box>
        {!isReadonly && (
          <button type="button" onClick={() => setModalOpen(true)} style={{
            display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '10px 18px', borderRadius: '8px',
            border: 'none', background: c.accent, color: 'white', fontSize: '13px', fontWeight: 600, cursor: 'pointer', flexShrink: 0,
          }}>
            <Plus size={16} />{tt('addShareholder', 'Agregar accionista')}
          </button>
        )}
      </Flex>

      {/* Banners */}
      {requiresEnhancedDueDiligence && (
        <Flex gap={3} p={3} mb={3} borderRadius="lg" bg="#FFF7ED" borderLeft="4px solid" borderColor="#FB923C">
          <AlertTriangle size={18} color="#a8740e" />
          <Box>
            <Text fontSize="sm" fontWeight="700" color="#8a5f0b">{tt('eddTitle', 'Debida Diligencia Intensificada (LBMA / OCDE)')}</Text>
            <Text fontSize="xs" color="#8a5f0b">{tt('eddBody', 'El sistema detectó antecedentes en uno o más beneficiarios. Esta solicitud será retenida preventivamente y pasará a revisión estricta por el Oficial de Cumplimiento.')}</Text>
          </Box>
        </Flex>
      )}
      {hasInvalid && (
        <Flex gap={3} p={3} mb={3} borderRadius="lg" bg="#fcefeb" borderLeft="4px solid" borderColor="#F87171">
          <CircleX size={18} color="#b23a26" />
          <Box>
            <Text fontSize="sm" fontWeight="700" color="#b23a26">{tt('invalidTitle', 'Documentos Inválidos o Rechazados')}</Text>
            <Text fontSize="xs" color="#b23a26">{tt('invalidBody', 'El sistema rechazó algunos certificados por inconsistencias (formato incorrecto o ilegibles). Vuelva a cargar el PDF original del certificado.')}</Text>
          </Box>
        </Flex>
      )}

      {/* Info de validación automática */}
      <Flex gap={2} p={3} mb={4} borderRadius="lg" bg="#eef3f9" alignItems="center">
        <Text fontSize="xs" color="#244775">
          <Text as="span" fontWeight="700">{tt('ocrTag', 'Validación automática:')}</Text>{' '}
          {tt('ocrBody', 'Al cargar el Certificado Único Laboral, el sistema lee el N° de documento del PDF, obtiene el nombre desde RENIEC y verifica el título, la firma del MTPE, la vigencia y los antecedentes policiales, judiciales y penales.')}
        </Text>
      </Flex>

      {/* Tabla */}
      <Box border="1px solid" borderColor={c.border} borderRadius="xl" overflow="hidden">
        <Box overflowX="auto">
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: c.bgHover }}>
                <th style={thStyle}>{tt('colDocType', 'Tipo Doc.')}</th>
                <th style={thStyle}>{tt('colDocNumber', 'Núm. Doc.')}</th>
                <th style={thStyle}>{tt('colName', 'Nombres y Apellidos')}</th>
                <th style={thStyle}>{tt('colPct', '% Part.')}</th>
                <th style={thStyle}>{tt('colCertificate', 'Certificado (PDF)')}</th>
                <th style={thStyle}>{tt('colValidation', 'Validación')}</th>
                {!isReadonly && <th style={{ ...thStyle, width: '40px' }} />}
              </tr>
            </thead>
            <tbody>
              {shareholders.map(sh => {
                const f = rowFields(sh)
                const st = statusColors(sh.validationStatus)
                const dirty = isDirty(sh)
                return (
                  <tr key={sh.id}>
                    <td style={tdStyle}>
                      <Text fontSize="sm" color={c.text}>{DOC_TYPE_LABEL[sh.documentType] ?? '-'}</Text>
                    </td>
                    <td style={tdStyle}>
                      <Text fontSize="sm" color={c.text} fontFamily="mono">{sh.documentNumber}</Text>
                    </td>
                    <td style={tdStyle}>
                      {isReadonly ? (
                        <Text fontSize="sm" color={c.text}>{sh.fullName || '-'}</Text>
                      ) : (
                        <div style={{ display: 'flex', gap: '4px' }}>
                          {/* Tope = columnas de Persons (100): al crear la persona el apellido se
                              parte en paterno/materno y un texto más largo abortaba la aprobación. */}
                          <input style={inputStyle} placeholder={tt('phNames', 'Nombres')} value={f.firstName} maxLength={100}
                            onChange={e => setEdit(sh.id, { firstName: e.target.value })} />
                          <input style={inputStyle} placeholder={tt('phSurnames', 'Apellidos')} value={f.lastName} maxLength={100}
                            onChange={e => setEdit(sh.id, { lastName: e.target.value })} />
                        </div>
                      )}
                    </td>
                    <td style={{ ...tdStyle, width: '90px' }}>
                      {isReadonly ? (
                        <Text fontSize="sm" color={c.text}>{sh.participationPercent}%</Text>
                      ) : (
                        <input type="number" min={0.01} max={100} step={0.01} style={inputStyle} value={f.participationPercent}
                          onChange={e => setEdit(sh.id, { participationPercent: e.target.value })} />
                      )}
                    </td>
                    <td style={tdStyle}>
                      <CertificateCell file={sh.certificateFile} isReadonly={isReadonly} busy={busy}
                        onPick={file => replaceCertificate(sh, file)} />
                    </td>
                    <td style={tdStyle}>
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 8px', borderRadius: '6px',
                        background: st.bg, color: st.color, fontSize: '11px', fontWeight: 700,
                      }}>{st.icon}{sh.validationStatusName}</span>
                      {sh.validationMessage && <div style={{ fontSize: '10px', color: c.textMuted, marginTop: '2px' }}>{sh.validationMessage}</div>}
                    </td>
                    {!isReadonly && (
                      <td style={{ ...tdStyle, textAlign: 'center' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'center' }}>
                          {dirty && (
                            <button type="button" title={tt('save', 'Guardar')} onClick={() => saveRow(sh)}
                              style={{ background: 'none', border: 'none', color: c.accent, cursor: 'pointer' }}><CircleCheck size={16} /></button>
                          )}
                          <button type="button" title={tt('remove', 'Eliminar')} onClick={() => removeRow(sh.id)}
                            style={{ background: 'none', border: 'none', color: c.textMuted, cursor: 'pointer' }}><Trash2 size={15} /></button>
                        </div>
                      </td>
                    )}
                  </tr>
                )
              })}

              {shareholders.length === 0 && (
                <tr>
                  <td colSpan={isReadonly ? 6 : 7} style={{ ...tdStyle, textAlign: 'center', padding: '24px' }}>
                    <Text fontSize="sm" color={c.textMuted}>{tt('empty', 'Aún no hay accionistas registrados. Use «Agregar accionista» para cargar su Certificado Único Laboral.')}</Text>
                  </td>
                </tr>
              )}
            </tbody>
            <tfoot>
              <tr style={{ background: c.bgHover }}>
                <td colSpan={3} style={{ ...tdStyle, textAlign: 'right', borderBottom: 'none' }}>
                  <Text fontSize="sm" fontWeight="700" color={c.text}>{tt('totalLabel', 'Participación Total Declarada:')}</Text>
                </td>
                <td colSpan={isReadonly ? 3 : 4} style={{ ...tdStyle, borderBottom: 'none' }}>
                  <Text fontSize="sm" fontWeight="800" color={exceeds ? '#b23a26' : '#1d6664'}>{total.toFixed(2)}%</Text>
                </td>
              </tr>
            </tfoot>
          </table>
        </Box>
      </Box>

      {exceeds && (
        <Flex mt={3} gap={1} alignItems="center" color="#b23a26">
          <CircleX size={14} />
          <Text fontSize="xs" fontWeight="600">{tt('exceedError', 'El porcentaje total no puede superar el 100%')}</Text>
        </Flex>
      )}

      {!isReadonly && (
        <AddShareholderModal
          open={modalOpen}
          currentTotal={total}
          busy={busy}
          onClose={() => setModalOpen(false)}
          onSubmit={async (file, pct) => {
            setBusy(true)
            try {
              await onAddFromCertificate(file, pct)
              setModalOpen(false)
            } finally { setBusy(false) }
          }}
        />
      )}
    </Box>
  )
}

// ── Modal de alta "PDF primero": certificado + % de participación ──
function AddShareholderModal({ open, currentTotal, busy, onClose, onSubmit }: {
  open: boolean
  currentTotal: number
  busy: boolean
  onClose: () => void
  onSubmit: (file: File, participationPercent: number) => Promise<void>
}) {
  const c = useFormColors()
  const { t } = useT()
  const fileRef = useRef<HTMLInputElement>(null)
  const [file, setFile] = useState<File | null>(null)
  const [pct, setPct] = useState('')

  const tt = (key: string, fallback: string) => {
    const full = `proveedores.wizard.step10.${key}`
    const r = t(full)
    return r === full ? fallback : r
  }

  const pctNum = Number(pct) || 0
  const wouldExceed = currentTotal + pctNum > 100.001
  const canSubmit = !!file && pct.trim() !== '' && pctNum > 0 && !wouldExceed && !busy

  const reset = () => { setFile(null); setPct(''); if (fileRef.current) fileRef.current.value = '' }
  const close = () => { reset(); onClose() }

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '10px 12px', fontSize: '14px', borderRadius: '8px',
    border: `1px solid ${c.border}`, background: c.bgInput, color: c.text, outline: 'none',
  }

  return (
    <Modal
      open={open}
      onClose={close}
      title={tt('modalTitle', 'Agregar accionista')}
      size="lg"
      footer={
        <Flex gap={3} justifyContent="flex-end">
          <button type="button" onClick={close} style={{
            padding: '8px 16px', borderRadius: '8px', border: `1px solid ${c.border}`,
            background: 'white', color: c.text, fontSize: '13px', fontWeight: 600, cursor: 'pointer',
          }}>{t('common.cancel')}</button>
          <button type="button" disabled={!canSubmit} onClick={() => file && onSubmit(file, pctNum)} style={{
            padding: '8px 16px', borderRadius: '8px', border: 'none',
            background: c.accent, color: 'white', fontSize: '13px', fontWeight: 600,
            cursor: canSubmit ? 'pointer' : 'not-allowed', opacity: canSubmit ? 1 : 0.5,
          }}>{busy ? tt('processing', 'Procesando...') : tt('addAndValidate', 'Agregar y validar')}</button>
        </Flex>
      }
    >
      <Box>
        <Text fontSize="sm" color={c.textSecondary} mb={4}>
          {tt('modalHint', 'Cargue el Certificado Único Laboral (PDF) del accionista. El sistema leerá automáticamente su DNI, obtendrá el nombre desde RENIEC y validará el certificado. Solo debe indicar el porcentaje de participación.')}
        </Text>

        {/* Carga del certificado */}
        <Box mb={4}>
          <Text fontSize="xs" fontWeight="600" color={c.textMuted} textTransform="uppercase" mb={2}>
            {tt('modalCertLabel', 'Certificado Único Laboral (PDF)')}
          </Text>
          <button type="button" onClick={() => fileRef.current?.click()} style={{
            width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
            padding: '20px', borderRadius: '10px', border: `2px dashed ${file ? c.accent : c.border}`,
            background: file ? '#ECFDF5' : c.bgInput, color: file ? '#047857' : c.textMuted,
            fontSize: '13px', fontWeight: 600, cursor: 'pointer',
          }}>
            {file ? <FileCheck size={18} /> : <Upload size={18} />}
            {file ? file.name : tt('modalCertPick', 'Seleccionar PDF del certificado')}
          </button>
          <input ref={fileRef} type="file" accept=".pdf,application/pdf" style={{ display: 'none' }}
            onChange={e => {
              const f = e.target.files?.[0]
              if (!f) return
              if (f.size === 0) { toaster.error({ description: 'El archivo está vacío (0 KB). Seleccione un archivo con contenido.' }); return }
              setFile(f)
            }} />
        </Box>

        {/* % participación */}
        <Box mb={2}>
          <Text fontSize="xs" fontWeight="600" color={c.textMuted} textTransform="uppercase" mb={2}>
            {tt('modalPctLabel', '% de Participación')}
          </Text>
          <input type="number" min={0} max={100} step="0.01" value={pct} placeholder="0.00"
            onChange={e => setPct(e.target.value)} style={inputStyle} />
          <Text fontSize="xs" color={c.textMuted} mt={1}>
            {tt('modalPctRemaining', 'Disponible')}: {(100 - currentTotal).toFixed(2)}%
          </Text>
        </Box>

        {wouldExceed && (
          <Flex gap={1} alignItems="center" color="#b23a26" mt={2}>
            <CircleX size={14} />
            <Text fontSize="xs" fontWeight="600">{tt('exceedError', 'El porcentaje total no puede superar el 100%')}</Text>
          </Flex>
        )}
      </Box>
    </Modal>
  )
}

// ── Celda de certificado: chip si existe, o botón de subida ──
function CertificateCell({ file, isReadonly, busy, onPick }: {
  file: { originalFileName: string } | null
  isReadonly: boolean
  busy: boolean
  onPick: (file: File) => void
}) {
  const c = useFormColors()
  const { t } = useT()
  const inputRef = useRef<HTMLInputElement>(null)
  const replaceLabel = (() => { const k = 'proveedores.wizard.step10.replaceCert'; const r = t(k); return r === k ? 'Reemplazar' : r })()

  const handle = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]
    e.target.value = ''
    if (!f) return
    if (f.size === 0) { toaster.error({ description: 'El archivo está vacío (0 KB). Seleccione un archivo con contenido.' }); return }
    onPick(f)
  }

  return (
    <div>
      {file ? (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '4px 8px', borderRadius: '6px', background: '#ECFDF5', maxWidth: '220px' }}>
          <span style={{ fontSize: '12px', color: '#047857', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file.originalFileName}</span>
          {!isReadonly && (
            <button type="button" title={replaceLabel} onClick={() => inputRef.current?.click()} style={{ background: 'none', border: 'none', color: '#047857', cursor: 'pointer', padding: 0 }}><Upload size={12} /></button>
          )}
        </span>
      ) : (
        <Text fontSize="xs" color={c.textMuted}>—</Text>
      )}
      <input ref={inputRef} type="file" accept=".pdf,application/pdf" style={{ display: 'none' }} disabled={busy} onChange={handle} />
    </div>
  )
}
